import { useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';

import { ATSConnectHiringRequestCreateReferenceContactIds } from 'data-testids/ATS';
import { Box } from '@mui/material';
import { ReferenceContactAndTemplateForm } from './components/ReferenceContactAndTemplateForm';
import { ReferenceCreateCTAButtons } from './components/ReferenceCreateCTAButtons';
import {
  ReferenceCreateLandingPageProps,
  ReferenceRequestInitialFormState,
  useCreateReference,
} from 'features/References';
import { ReferenceRequestPayload } from 'features/References';
import { ReferenceHeader } from './components/ReferenceHeader';
import { ReferenceToggle } from './components/ReferenceToggle';
import { RequestReferenceCheck } from './Request/RequestReferenceCheck';
import { ToggleButtonAlignment } from 'sharedComponents/Buttons/ToggleButtons';
import { ToggleButtonCurrentlySelected } from 'sharedComponents/Buttons/types';
import { WriteReferenceCheck } from './Write/WriteReferenceCheck';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { useCreateReferenceRequest, useEditReference } from 'features/References';
import {
  candidateFeedbackToasts,
  HiringRequestToast,
} from '../../../Connect/features/ConnectATSIntegration/enums';
import { ReferenceCheck } from 'types';
import auth from 'utils/auth';

export const ReferenceCreateLandingPage: React.FC<ReferenceCreateLandingPageProps> = ({
  hiringRequestId,
  newReferenceContact,
  onAddClick,
  onCancel,
  setFeedbackModalToast,
  referenceId,
  roleTitle,
  candidate,
}) => {
  const [currentReferenceToggle, setCurrentReferenceToggle] =
    useState<ToggleButtonCurrentlySelected>(ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE);
  const [draftReferenceId, setDraftReferenceId] = useState<number | null>(null);
  const districtId = auth.getUser()?.profile?.district?.id;

  const createRequestMethods = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      candidate_id: candidate.id,
      subject: '',
      message: '',
      recipients: newReferenceContact,
      reference_id: newReferenceContact?.id,
    },
  });

  const { handleSubmit: handleRequestSubmit } = createRequestMethods;
  const {
    mutate: createReferenceRequest,
    error: createReferenceRequestError,
    isLoading,
  } = useCreateReferenceRequest(candidate.id, {
    onSuccess: () => {
      onCancel(); // returns modal to list view
      setFeedbackModalToast({
        type: HiringRequestToast.SUCCESS,
        message: candidateFeedbackToasts.referenceRequest,
      });
    },
    onError: () => {
      setFeedbackModalToast({
        type: HiringRequestToast.ERROR,
        message: getAxiosErrorMessageByStatusCode(createReferenceRequestError),
      });
    },
  });

  const createReferenceMethods = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      reference_id: newReferenceContact?.id,
    },
  });
  const { handleSubmit: handleWriteSubmit } = createReferenceMethods;

  const { mutate: createReferenceCheck, error: createReferenceError } = useCreateReference(
    candidate.id,
    districtId,
    {
      onSuccess: (data: ReferenceCheck) => {
        onCancel(); // returns modal to list view
        if (!data.is_complete) {
          setDraftReferenceId(data.id);
          setFeedbackModalToast({
            type: HiringRequestToast.SUCCESS,
            message: candidateFeedbackToasts.referenceDraft,
          });
        } else {
          setFeedbackModalToast({
            type: HiringRequestToast.SUCCESS,
            message: candidateFeedbackToasts.referenceSuccess,
          });
        }
      },
      onError: () => {
        setFeedbackModalToast({
          type: HiringRequestToast.ERROR,
          message: getAxiosErrorMessageByStatusCode(createReferenceError),
        });
      },
    }
  );

  const { mutate: editReferenceCheck, error: editReferenceError } = useEditReference(
    candidate.id,
    districtId,
    draftReferenceId,
    {
      onSuccess: (data: ReferenceCheck) => {
        onCancel(); // returns modal to list view
        if (!data.is_complete) {
          setFeedbackModalToast({
            type: HiringRequestToast.SUCCESS,
            message: candidateFeedbackToasts.referenceDraft,
          });
        } else {
          setFeedbackModalToast({
            type: HiringRequestToast.SUCCESS,
            message: candidateFeedbackToasts.referenceSuccess,
          });
        }
      },
      onError: () => {
        setFeedbackModalToast({
          type: HiringRequestToast.ERROR,
          message: getAxiosErrorMessageByStatusCode(editReferenceError),
        });
      },
    }
  );

  const handleToggle = () => {
    if (currentReferenceToggle === ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE) {
      setCurrentReferenceToggle(ToggleButtonAlignment.RIGHT_TOGGLE_ACTIVE);
    } else {
      setCurrentReferenceToggle(ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE);
    }
  };

  const handleReferenceSubmit = (formState, isComplete = true) => {
    const { recipients, ...formStateWithoutRecipients } = formState;
    const formData = new FormData();
    const referenceId = recipients?.id;

    const processedAnswers = formState.answers.map((answer) => {
      if (answer.attachment instanceof File) {
        formData.append(answer.attachment.name, answer.attachment);
        return {
          ...answer,
          attachment: answer.attachment.name,
        };
      }
      return answer;
    });

    const payload = {
      ...formStateWithoutRecipients,
      answers: processedAnswers,
      is_complete: isComplete,
      candidate_id: candidate.id,
      submitted_by_name: recipients?.name || newReferenceContact?.name,
      reference_id: referenceId,
      submitted_by_title: '',
    };

    formData.append('data', JSON.stringify(payload));

    if (draftReferenceId) {
      editReferenceCheck({
        candidateId: candidate.id,
        referenceId: draftReferenceId,
        payload: formData,
        isComplete,
      });
    } else {
      createReferenceCheck({ payload: formData });
    }
  };

  const handleCreateRequest = (formState: ReferenceRequestInitialFormState | FieldValues) => {
    const formStateToSubmit: ReferenceRequestPayload = {
      ...(formState as ReferenceRequestInitialFormState),
      recipients: [formState.recipients],
      role_title: '',
      is_reminder: false,
    };

    createReferenceRequest(formStateToSubmit);
  };

  return (
    <Box data-testid={ATSConnectHiringRequestCreateReferenceContactIds.LANDING_PAGE}>
      <ReferenceHeader candidateName={`${candidate.first_name} ${candidate.last_name}`} />
      <Box sx={{ py: 2 }}>
        {currentReferenceToggle === ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE && (
          <FormProvider {...createReferenceMethods}>
            <form
              onSubmit={handleWriteSubmit((formState) => handleReferenceSubmit(formState, true))}
            >
              <ReferenceContactAndTemplateForm
                candidateId={candidate.id}
                isCreatingNewReferenceCheck={true}
                newReferenceContactId={newReferenceContact?.id}
                onAddClick={onAddClick}
              />
              <ReferenceToggle currentlySelected={currentReferenceToggle} onToggle={handleToggle} />
              <WriteReferenceCheck />
              <ReferenceCreateCTAButtons
                isLoading={isLoading}
                onBackClick={onCancel}
                onSaveAsDraft={handleWriteSubmit((formState) => {
                  handleReferenceSubmit(formState, false);
                })}
                isCreatingNewReferenceCheck={true}
              />
            </form>
          </FormProvider>
        )}
        {currentReferenceToggle === ToggleButtonAlignment.RIGHT_TOGGLE_ACTIVE && (
          <FormProvider {...createRequestMethods}>
            <form onSubmit={handleRequestSubmit(handleCreateRequest)}>
              <ReferenceContactAndTemplateForm
                candidateId={candidate.id}
                isCreatingNewReferenceCheck={false}
                newReferenceContactId={newReferenceContact?.id}
                onAddClick={onAddClick}
              />
              <ReferenceToggle currentlySelected={currentReferenceToggle} onToggle={handleToggle} />
              <RequestReferenceCheck
                referenceId={referenceId}
                hiringRequestId={hiringRequestId}
                roleTitle={roleTitle}
                candidate={candidate}
              />
              <ReferenceCreateCTAButtons
                isLoading={isLoading}
                onBackClick={onCancel}
                isCreatingNewReferenceCheck={false}
              />
            </form>
          </FormProvider>
        )}
      </Box>
    </Box>
  );
};
